// ============================================================
// SecaoPreparador.js — Painel do Preparador Físico / Auxiliar de Preparação
//
// Só leitura. Mostra TODOS os atletas do núcleo, AGRUPADOS por modalidade,
// com a frequência de cada um no mês escolhido — usando a MESMA função de
// calculo.js (fonte única, nenhuma lógica duplicada). O preparo físico é
// transversal às modalidades, por isso vê o núcleo inteiro.
//
// Não cadastra, não edita, não gera Excel, não faz comparativo — só
// acompanha os indicadores e o histórico mensal dos atletas.
// ============================================================

import { useState, useEffect } from "react";
import { carregarDispensasDoMes, carregarPresencasDoMes, carregarNomesAtuais } from "../firebase";
import { calcularFrequencia, normalizarModalidade } from "../calculo";
import { MESES, CINZA_ESC, TEXTO, BRANCO, AZUL, argbParaCss } from "../tema";
import { Card, Sel, StatusBox } from "../componentes";
import LegendaClassificacao from "../componentes/LegendaClassificacao";
import useIsMobile from "../useIsMobile";

export default function SecaoPreparador({ userNucleo }) {
  const isMobile = useIsMobile();
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth();
  const [mes, setMes] = useState(String(mesAtual));
  const [ano, setAno] = useState(String(anoAtual));
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [grupos, setGrupos] = useState([]); // [{ modalidade, atletas: [...] }]
  const [totalTreinos, setTotalTreinos] = useState(0);
  // Dia corrente ainda não fechado (antes das 18h30): aparece na grade,
  // mas não entra em totalTreinos. Guardado para o rótulo dizer isso.
  const [hojeEmAndamento, setHojeEmAndamento] = useState(false);

  const anos = [anoAtual - 1, anoAtual, anoAtual + 1].map(String);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const mesNum = parseInt(mes);
        const anoNum = parseInt(ano);

        // O banco filtra o mês, e não o navegador: antes vinham 2.499
        // documentos para mostrar os 434 do mês.
        const registrosDoMes = await carregarPresencasDoMes(userNucleo, mesNum, anoNum);

        const dispensasDoMes = await carregarDispensasDoMes(userNucleo, mesNum, anoNum);

        // O nome do CADASTRO, e não o que ficou gravado no registro de
        // presença. Faltava aqui: para quem foi renomeado, a dispensa
        // escrita com o nome novo não casava com o nome velho do registro,
        // e esta tela mostrava uma frequência diferente da da coordenação
        // para a mesma pessoa no mesmo mês.
        const nomesAtuais = await carregarNomesAtuais(userNucleo);

        const calc = calcularFrequencia({ registros: registrosDoMes, dispensas: dispensasDoMes, mes: mesNum, ano: anoNum, nomesAtuais });

        // Todos os atletas, agrupados por modalidade. Dentro de cada grupo,
        // pior frequência primeiro (mesma lógica da tela do técnico).
        const atletas = calc.membros.filter(m => m.grupo === "ATLETA");
        const porModalidade = {};
        atletas.forEach(m => {
          const mod = normalizarModalidade(m.modalidade) || "Sem modalidade";
          if (!porModalidade[mod]) porModalidade[mod] = [];
          porModalidade[mod].push(m);
        });
        const listaGrupos = Object.entries(porModalidade)
          .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
          .map(([modalidade, lista]) => ({
            modalidade,
            atletas: lista.sort((a, b) => a.pctTotal - b.pctTotal),
          }));

        if (ativo) {
          setGrupos(listaGrupos);
          setTotalTreinos(calc.totalTreinos);
          setHojeEmAndamento(Boolean(calc.diaHojeAguardando));
        }
      } catch (e) {
        if (ativo) setErro("Erro ao carregar dados: " + (e.message || ""));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => { ativo = false; };
  }, [mes, ano, userNucleo]);

  const totalAtletas = grupos.reduce((s, g) => s + g.atletas.length, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
          Frequência dos atletas · todas as modalidades
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Sel label="Mês" value={mes} onChange={setMes}
            options={MESES.map((m, i) => ({ value: String(i), label: m }))} />
          <Sel label="Ano" value={ano} onChange={setAno} options={anos} />
        </div>
      </Card>

      {erro && <StatusBox tipo="erro" msg={erro} />}

      {carregando ? (
        <Card><div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando...</div></Card>
      ) : totalAtletas === 0 ? (
        <Card><div style={{ fontSize: 13, color: CINZA_ESC }}>Nenhum atleta com registros neste mês.</div></Card>
      ) : (
        <>
          <div style={{ fontSize: 12, color: CINZA_ESC }}>
            {totalTreinos} dias de treino no mês
            {hojeEmAndamento ? " (hoje ainda em andamento)" : ""}
            {" · "}{totalAtletas} atleta{totalAtletas !== 1 ? "s" : ""} em {grupos.length} modalidade{grupos.length !== 1 ? "s" : ""}
          </div>
          {grupos.map(g => (
            <Card key={g.modalidade}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: AZUL }}>{g.modalidade}</span>
                <span style={{ fontSize: 12, color: CINZA_ESC }}>
                  {g.atletas.length} atleta{g.atletas.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {g.atletas.map(m => (
                  <div key={m.nome} style={{ border: `1.5px solid ${argbParaCss(m.classif.cor)}55`, background: `${argbParaCss(m.classif.bg)}`, borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: TEXTO }}>{m.nome}</div>
                      <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 2 }}>
                        Presenças: {m.presEfetiva} · Faltas justificadas: {m.jc + m.jm + m.ji} · Faltas não justificadas: {m.faltas}
                      </div>
                    </div>
                    <div style={{ textAlign: "center", minWidth: 90 }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: argbParaCss(m.classif.cor) }}>
                        {Math.round(m.pctTotal * 100)}%
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: BRANCO, color: argbParaCss(m.classif.cor), marginTop: 2, whiteSpace: "nowrap" }}>
                        {m.classif.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </>
      )}

      <Card>
        <LegendaClassificacao />
      </Card>
    </div>
  );
}
